import React, { useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useLocaleState, useRedirect, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import AddIcon from '@mui/icons-material/Add'
import { AccountSearch } from './AdminSearch'
import welcomeIllustration from './welcome_illustration.svg'

const ADD_ICON = <AddIcon fontSize="small" />
const SEARCH_STYLE: React.CSSProperties = { width: '100%' }

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 24px',
  },
  card: {
    width: '100%',
    maxWidth: 960,
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 16px 30px rgba(35, 0, 96, 0.08)',
    padding: '40px 48px',
    display: 'flex',
    alignItems: 'center',
    gap: 32,
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      padding: 24,
      gap: 16,
    },
  },
  copy: {
    flex: '1 1 320px',
    minWidth: 0,
  },
  title: {
    fontSize: 32,
    fontWeight: 700,
    color: '#211F1F',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#5B5B66',
    marginBottom: 24,
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    color: '#6E67EE',
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  ctaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  addButton: {
    backgroundColor: '#1B7F3A',
    color: '#fff',
    textTransform: 'none',
    borderRadius: 999,
    padding: '10px 20px',
    fontWeight: 600,
    boxShadow: '0 6px 12px rgba(27, 127, 58, 0.25)',
    '&:hover': {
      backgroundColor: '#196D33',
    },
  },
  openWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 280,
    flex: '1 1 280px',
  },
  openLabel: {
    fontSize: 13,
    color: '#5B5B66',
    fontWeight: 500,
  },
  searchInput: {
    borderRadius: 10,
    backgroundColor: '#fff',
    '&[class*=MuiOutlinedInput-root]': {
      borderRadius: 10,
      backgroundColor: '#fff',
    },
  },
  illustration: {
    flex: '0 0 240px',
    width: 240,
    height: 180,
    backgroundImage: `url(${welcomeIllustration})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: 12,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}))

const WelcomeNoPortfolio: React.FC = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const redirectTo = useRedirect()
  const [locale] = useLocaleState()
  const fullName = useSelector(
    (state: any) =>
      state?.profile?.user?.UserPersonal?.FullName?.[locale] ||
      state?.profile?.user?.UserPersonal?.FullName?.en ||
      ''
  )

  const handleAddPortfolio = useCallback(() => {
    redirectTo('/accountCreate')
  }, [redirectTo])

  return (
    <Box className={classes.root}>
      <Box className={classes.card}>
        <Box className={classes.copy}>
          {fullName && <Typography className={classes.name}>{fullName}</Typography>}
          <Typography className={classes.title} component="h1">
            {translate('pos.dashboard.no_portfolio.title')}
          </Typography>
          <Typography className={classes.subtitle}>
            {translate('pos.dashboard.no_portfolio.subtitle')}
          </Typography>
          <Box className={classes.ctaRow}>
            <Button
              className={classes.addButton}
              onClick={handleAddPortfolio}
              startIcon={ADD_ICON}
            >
              {translate('pos.dashboard.no_portfolio.add_cta')}
            </Button>
            <Box className={classes.openWrap}>
              <Typography className={classes.openLabel}>
                {translate('pos.dashboard.no_portfolio.open_label')}
              </Typography>
              <AccountSearch
                isBarSearch
                style={SEARCH_STYLE}
                variant="outlined"
                inputRootClass={classes.searchInput}
              />
            </Box>
          </Box>
        </Box>
        <Box className={classes.illustration} aria-hidden />
      </Box>
    </Box>
  )
}

export default React.memo(WelcomeNoPortfolio)
